import TEXT_EN from "./text-en.json";

(() => {
  "use strict";

  const q = new URLSearchParams(location.search);
  const cfg = window.__AIGC || {};
  const DATA_URL = q.get("data") || cfg.dataUrl || "data/dashboard.json";
  const MEDIA_BASE = String(q.get("media") || cfg.mediaBase || "media").replace(/\/$/, "");
  const MEDIA_PROXY = String(cfg.mediaProxyBase || "").replace(/\/$/, "");
  const STATIC_MEDIA = new Set(
    Array.isArray(cfg.staticMediaBatches) && cfg.staticMediaBatches.length
      ? cfg.staticMediaBatches
      : ["v_formal", "formal", "route_ablation_g127"]
  );
  /** loading | ok | fail — chrome status, not recipe text */
  let loadState = "loading";

  function t(key) {
    const I = window.__AIGC_I18N;
    return I && typeof I.t === "function" ? I.t(key) : key;
  }

  function timeLocale() {
    const I = window.__AIGC_I18N;
    return I && I.getLocale() === "en" ? "en-GB" : "zh-CN";
  }

  function hasHan(s) {
    return /[\u3400-\u9FFF\uF900-\uFAFF]/.test(String(s || ""));
  }
  /** Data text language follows the UI toggle. */
  function lang() {
    const I = window.__AIGC_I18N;
    return I && I.getLocale() === "en" ? "en" : "zh";
  }

  function hasText(v) {
    if (Array.isArray(v)) return v.length > 0;
    return String(v ?? "").trim() !== "";
  }

  /**
   * Bilingual field: Chinese lives in `field`, English in `field_en`.
   * Current language first, then whichever exists, never blank when one side has text.
   */
  function loc(obj, field) {
    if (!obj) return "";
    const zh = obj[field];
    const en = obj[`${field}_en`];
    const [first, second] = lang() === "en" ? [en, zh] : [zh, en];
    if (hasText(first)) return first;
    if (hasText(second)) return second;
    return Array.isArray(first) || Array.isArray(second) ? [] : "";
  }

  /** Fill *_en from the bundled English copy (src/text-en.json) where the data has none. */
  function mergeTextEn() {
    const src = (TEXT_EN && TEXT_EN.batches) || {};
    for (const batch of data.batches || []) {
      const e = src[batch.batch_id];
      for (const f of ["title_en", "hypothesis_en", "findings_en"]) {
        if (e && !hasText(batch[f]) && hasText(e[f])) batch[f] = e[f];
      }
      for (const job of batch.jobs || []) {
        // Upload path stores the English condition name as title_en
        if (!hasText(job.cond_label_en) && hasText(job.title_en)) job.cond_label_en = job.title_en;
        const je = e && e.jobs && e.jobs[job.job_id];
        if (je && !hasText(job.cond_label_en) && hasText(je.cond_label_en)) job.cond_label_en = je.cond_label_en;
      }
    }
  }

  const LS_ANNOT = "aigc.annotations";
  const LS_HYPO = "aigc.batchHypothesis";
  const NOTE_MAX = 80;
  const NOTE_COUNT_AT = 60;
  const DEBOUNCE_MS = 300;
  const SAVED_FLASH_MS = 1200;
  /** Fixed page size + fixed row height (CSS --row-h): pages never reflow on resize. */
  const ROWS_PER_PAGE = 20; // Zed: 20 per page, list scrolls inside its column
  const PAGER_MAX_NUMBERS = 10;
  const VERSION = "v1.3.31-staging";

  const QC_LABEL = { pass: "Pass", partial: "Partial", fail: "Fail" };
  const ROUTE_LABEL = { i2v: "I2V", omni: "Omni" };

  /** @type {any} */
  let data = null;
  /** @type {string|null} */
  let selectedBatchId = null;
  /** @type {string|null} */
  let selectedJobId = null;
  /** Skip full rebuild when only annotation chrome changes */
  let detailJobRendered = null;
  let currentPage = 1;
  /** batch + page + filters of the rendered list; a change resets the list's scroll to the top */
  let listKey = "";
  /** Which Detail popover is open: null | "note" | "tech" */
  let openPop = null;
  let noteDebounceTimer = null;
  let savedFlashTimer = null;

  const $ = (id) => document.getElementById(id);

  function escapeHtml(str) {
    return String(str ?? "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;");
  }

  function driveFileUrl(fileId) {
    return `https://drive.google.com/file/d/${fileId}/view`;
  }

  function driveFolderUrl(folderId) {
    return `https://drive.google.com/drive/folders/${folderId}`;
  }

  /** Lucide-style inline SVGs (24 viewBox; CSS sizes 16|20). Stroke 1.75, round caps. */
  const ICON_PLAY = `<svg class="icon icon-16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M22 12C22 17.5228 17.5228 22 12 22C6.4772 22 2 17.5228 2 12C2 6.4772 6.4772 2 12 2C17.5228 2 22 6.4772 22 12ZM8.962734 9.105541C8.962734 8.320105 9.826684 7.841262 10.492732 8.257542L15.802266 11.576001C16.1156 11.771835 16.1156 12.228165 15.802266 12.423999L10.492732 15.742458C9.826684 16.158738 8.962734 15.679895 8.962734 14.894459Z"/></svg>`;
  const ICON_EXT = `<svg class="icon icon-16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M8.1883 18.4383C7.5084 19.1181 6.5864 19.5 5.625 19.5C4.6636 19.5 3.7416 19.1181 3.0617 18.4383C2.3819 17.7584 2 16.8364 2 15.875C2 14.9136 2.3819 13.9916 3.0617 13.3117L8.1867 8.1867C8.8666 7.5069 9.7886 7.125 10.75 7.125C11.7114 7.125 12.6334 7.5069 13.3133 8.1867C13.9931 8.8666 14.375 9.7886 14.375 10.75C14.375 11.7114 13.9931 12.6334 13.3133 13.3133M15.8117 5.5617C16.4916 4.8819 17.4136 4.5 18.375 4.5C19.3364 4.5 20.2584 4.8819 20.9383 5.5617C21.6181 6.2416 22 7.1636 22 8.125C22 9.0864 21.6181 10.0084 20.9383 10.6883L15.8133 15.8133C15.1334 16.4931 14.2114 16.875 13.25 16.875C12.2886 16.875 11.3666 16.4931 10.6867 15.8133C10.0069 15.1334 9.625 14.2114 9.625 13.25C9.625 12.2886 10.0069 11.3666 10.6867 10.6867"/></svg>`;
  const ICON_PREV = `<svg class="icon icon-16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="15 18 9 12 15 6"/></svg>`;
  const ICON_NEXT = `<svg class="icon icon-16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="9 18 15 12 9 6"/></svg>`;
  const ICON_CHEVRON = `<svg class="icon icon-16 tech-chevron" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="6 9 12 15 18 9"/></svg>`;

  function mediaUrls(batchId, fileName) {
    if (!batchId || !fileName) return null;
    const tail = `/${encodeURIComponent(batchId)}/${encodeURIComponent(fileName)}`;
    const onPages = !MEDIA_PROXY || STATIC_MEDIA.has(batchId);
    const primary = (onPages ? MEDIA_BASE : MEDIA_PROXY) + tail;
    const fallback = onPages && MEDIA_PROXY ? MEDIA_PROXY + tail : "";
    return { primary, fallback };
  }

  function localMediaUrl(batchId, fileName) {
    const u = mediaUrls(batchId, fileName);
    return u ? u.primary : null;
  }

  function formatBytes(n) {
    if (n == null || Number.isNaN(n)) return "—";
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  }

  function formatTime(iso) {
    if (!iso) return "—";
    try {
      const d = new Date(iso);
      if (Number.isNaN(d.getTime())) return iso;
      return d.toLocaleString(timeLocale(), { hour12: false });
    } catch {
      return iso;
    }
  }

  function titleCase(str) {
    return String(str || "")
      .replace(/[_-]+/g, " ")
      .trim()
      .split(/\s+/)
      .filter(Boolean)
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
      .join(" ");
  }

  function sentenceCase(str) {
    const s = String(str || "").replace(/_+/g, " ").replace(/\s+/g, " ").trim();
    return s ? s.charAt(0).toUpperCase() + s.slice(1) : "";
  }

  function qcLabel(v) {
    if (v === "pass" || v === "partial" || v === "fail") return t(v);
    if (v === "pending") return t("qcPending");
    return v || "—";
  }

  function routeLabel(v) {
    return ROUTE_LABEL[v] || v || "—";
  }

  /** Visible badge/filter label: localized model (model_en in EN), falls back to model. */
  function platformModelLabel(job) {
    return String(loc(job, "model") || "").trim() || "—";
  }

  /** Bold row label: random upload ids (up_…) show the localized title instead; readable ids stay. */
  function rowLabel(job) {
    if (!/^up_/.test(job.job_id || "")) return job.job_id;
    return String(loc(job, "title") || loc(job, "cond_label") || "").trim() || job.job_id;
  }

  /** Tooltip for the row label: full label plus job_id when they differ. */
  function rowLabelTip(job) {
    const label = rowLabel(job);
    return label === job.job_id ? job.job_id : `${label} · ${job.job_id}`;
  }

  /** List-row model line: name truncates, a trailing （…）/(…) note stays visible. */
  function modelSubHtml(label) {
    const m = label.match(/^(.*?)(\s*[（(][^（()）]*[)）])$/);
    // leading space -> nbsp: flex items drop plain leading whitespace
    const [name, suffix] = m && m[1] ? [m[1], m[2].replace(/^\s+/, "\u00a0")] : [label, ""];
    return `<span class="ml-name">${escapeHtml(name)}</span>` + (suffix ? `<span class="ml-suffix">${escapeHtml(suffix)}</span>` : "");
  }

  /** Platform·model filter options from the data: value = raw model, label = localized. */
  function renderModelFilter() {
    const sel = $("filter-route");
    if (!sel || !data) return;
    const labels = new Map();
    for (const b of visibleBatches()) {
      for (const j of b.jobs || []) {
        if (j.model && !labels.has(j.model)) labels.set(j.model, platformModelLabel(j));
      }
    }
    const prev = sel.value;
    sel.innerHTML =
      `<option value="">${escapeHtml(t("all"))}</option>` +
      [...labels].map(([v, l]) => `<option value="${escapeHtml(v)}">${escapeHtml(l)}</option>`).join("");
    sel.value = labels.has(prev) ? prev : "";
  }

  /** Factor: `{cond} · {TitleCase(cond_label)}` e.g. `G7 · Orbit` */
  function factorLabel(job) {
    const cond = job.cond || "—";
    const raw = String(loc(job, "cond_label") || "");
    // Chinese as authored; English: underscores to spaces, first letter up, keep acronyms (LUT)
    const label = !raw ? "" : hasHan(raw) ? raw : sentenceCase(raw);
    return label ? `${cond} · ${label}` : cond;
  }

  function optionalEnglishBits(job) {
    return [job.product_en, job.axis_en, job.title_en]
      .map((s) => String(s || "").trim())
      .filter(Boolean);
  }

  /** Tech-popover rows for upload fields (v1.3.30: moved out of Detail). Omit the English row when every English field is empty. */
  function uploadIdentityRows(job) {
    let html = "";
    const product = String(loc(job, "product") || "").trim();
    if (product) html += `<dt>${escapeHtml(t("productShort"))}</dt><dd>${escapeHtml(product)}</dd>`;
    const ens = optionalEnglishBits(job);
    if (ens.length) html += `<dt>${escapeHtml(t("enLine"))}</dt><dd>${escapeHtml(ens.join(" · "))}</dd>`;
    return html;
  }

  function resolvePreviz(job) {
    const assets = data && data.previz_assets ? data.previz_assets : [];
    const feat = job.feature_video;
    if (!feat) return null;
    const hit =
      assets.find((a) => a.file === feat || a.id === feat || a.name === feat) ||
      null;
    if (hit) return { name: hit.name || hit.id, file: hit.file || feat };
    const base = String(feat).replace(/\.mp4$/i, "").replace(/^previz_/, "");
    return { name: base, file: feat };
  }

  function annotKey(batchId, jobId) {
    return `${batchId}::${jobId}`;
  }

  function loadAnnotStore() {
    try {
      const raw = localStorage.getItem(LS_ANNOT);
      if (!raw) return {};
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === "object" ? parsed : {};
    } catch {
      return {};
    }
  }

  function saveAnnotStore(store) {
    localStorage.setItem(LS_ANNOT, JSON.stringify(store));
  }

  function loadHypoStore() {
    try {
      const raw = localStorage.getItem(LS_HYPO);
      if (!raw) return {};
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === "object" ? parsed : {};
    } catch {
      return {};
    }
  }

  function hypoEntry(v) {
    if (typeof v === "string") return { zh: v };
    return v && typeof v === "object" ? { ...v } : {};
  }

  function oneLine(s) {
    return String(s || "").replace(/[\r\n]+/g, " ").replace(/\s+/g, " ").trim();
  }

  function applyLocalOverrides() {
    if (!data || !data.batches) return;
    const store = loadAnnotStore();
    const hypos = loadHypoStore();
    mergeTextEn();
    for (const batch of data.batches) {
      // Hypothesis edits are per language; a legacy plain string was a Chinese edit
      const h = hypoEntry(hypos[batch.batch_id]);
      if (typeof h.zh === "string") batch.hypothesis = h.zh;
      if (typeof h.en === "string") batch.hypothesis_en = h.en;
      for (const job of batch.jobs || []) {
        const o = store[annotKey(batch.batch_id, job.job_id)];
        if (!o) continue;
        // "pending" is an explicit local undo (also over a judged server value); no key = no override
        if (o.qc_status === "pass" || o.qc_status === "partial" || o.qc_status === "fail" || o.qc_status === "pending") {
          job.qc_status = o.qc_status;
        }
        if (typeof o.qc_notes === "string") {
          job.qc_notes = o.qc_notes;
        }
      }
    }
  }

  function persistAnnotation(batchId, jobId, patch) {
    const batch = (data.batches || []).find((b) => b.batch_id === batchId);
    const job = batch && (batch.jobs || []).find((j) => j.job_id === jobId);
    if (!batch || !job) return { ok: false, error: "job missing" };
    if (batch.batch_status === "deprecated") {
      return { ok: false, error: "deprecated read-only" };
    }

    if (patch.qc_status != null) job.qc_status = patch.qc_status;
    if (patch.qc_notes != null) job.qc_notes = String(patch.qc_notes).slice(0, NOTE_MAX);

    try {
      const store = loadAnnotStore();
      store[annotKey(batchId, jobId)] = {
        qc_status: job.qc_status,
        qc_notes: job.qc_notes || "",
      };
      saveAnnotStore(store);
      return { ok: true };
    } catch (err) {
      return { ok: false, error: err && err.message ? err.message : "save failed" };
    }
  }

  function getFilters() {
    return {
      batchStatus: $("filter-batch-status").value,
      model: $("filter-route").value,
      qc: $("filter-qc").value,
      cond: $("filter-cond").value,
    };
  }

  /** Hide smoke/test batches from the main compare selector. */
  function isHiddenTestBatch(b) {
    const id = String((b && b.batch_id) || "");
    if (id.startsWith("upload_smoke") || id.startsWith("upload_")) return true;
    if (b && (b.batch_status === "smoke" || b.visibility === "smoke")) return true;
    return false;
  }

  function visibleBatches() {
    return (data.batches || []).filter((b) => !isHiddenTestBatch(b));
  }

  function filteredBatches() {
    const f = getFilters();
    return visibleBatches().filter((b) => {
      if (f.batchStatus && b.batch_status !== f.batchStatus) return false;
      return true;
    });
  }

  function getSelectedBatch() {
    return (data.batches || []).find((b) => b.batch_id === selectedBatchId) || null;
  }

  function getSelectedJob() {
    const batch = getSelectedBatch();
    if (!batch || !selectedJobId) return null;
    return (batch.jobs || []).find((j) => j.job_id === selectedJobId) || null;
  }

  function filteredJobs(batch) {
    if (!batch) return [];
    const f = getFilters();
    return (batch.jobs || []).filter((j) => {
      if (f.model && j.model !== f.model) return false;
      if (f.qc && j.qc_status !== f.qc) return false;
      if (f.cond && j.cond !== f.cond) return false;
      return true;
    });
  }

  function renderTopbar() {
    const nameEl = $("project-name");
    if (nameEl) nameEl.textContent = t("projectTitle");
  }

  function batchTitle(b) {
    const title = String(loc(b, "title") || "").trim();
    return title || (b && b.batch_id) || "";
  }

  /** Top-bar Batch selector (no status pill: removed per JW); selecting switches jobs. */
  function renderBatchSelector() {
    const sel = $("batch-select");
    if (!sel) return;

    const batches = filteredBatches();
    if (!batches.length) {
      sel.innerHTML = `<option value="">${escapeHtml(t("noBatches"))}</option>`;
      sel.disabled = true;
      selectedBatchId = null;
      selectedJobId = null;
      detailJobRendered = null;
      return;
    }

    sel.disabled = false;
    if (!batches.some((b) => b.batch_id === selectedBatchId)) {
      const active = batches.find((b) => b.batch_status !== "deprecated") || batches[0];
      selectedBatchId = active.batch_id;
      selectedJobId = null;
      detailJobRendered = null;
    }

    const prev = sel.value;
    sel.innerHTML = batches
      .map((b) => {
        const n = (b.jobs || []).length;
        const label = `${batchTitle(b)} (${n})`;
        return `<option value="${escapeHtml(b.batch_id)}">${escapeHtml(label)}</option>`;
      })
      .join("");
    sel.value = selectedBatchId;
    if (sel.value !== selectedBatchId && batches[0]) {
      sel.value = batches[0].batch_id;
      selectedBatchId = batches[0].batch_id;
    }
    sel.title = sel.selectedOptions[0] ? sel.selectedOptions[0].textContent : "";

    // Rebind once: avoid stacking listeners — use onchange property
    sel.onchange = () => {
      const next = sel.value;
      if (!next || next === selectedBatchId) return;
      selectedBatchId = next;
      selectedJobId = null;
      detailJobRendered = null;
      currentPage = 1;
      renderAll();
    };

    void prev;
  }

  function promptRow(job) {
    return job.prompt_id != null && String(job.prompt_id).trim() !== ""
      ? `<div class="job-identity-row"><span class="job-k">${escapeHtml(t("prompt"))}</span><span class="job-v mono">${escapeHtml(job.prompt_id)}</span></div>`
      : "";
  }

  function pageCount(n) {
    return Math.max(1, Math.ceil(n / ROWS_PER_PAGE));
  }

  function renderJobs() {
    const batch = getSelectedBatch();
    const list = $("job-list");
    const empty = $("jobs-empty");

    if (!batch) {
      paintBatchTitle(null);
      paintBatchHypothesis(null);
      list.innerHTML = "";
      renderPager(0);
      empty.textContent = t("emptySelectBatch");
      empty.classList.remove("hidden");
      return;
    }

    const jobs = filteredJobs(batch);
    paintBatchTitle(batch);
    paintBatchHypothesis(batch);

    if (!jobs.length) {
      list.innerHTML = "";
      renderPager(0);
      empty.textContent = t("noJobs");
      empty.classList.remove("hidden");
      return;
    }
    empty.classList.add("hidden");

    currentPage = Math.min(Math.max(1, currentPage), pageCount(jobs.length));
    const start = (currentPage - 1) * ROWS_PER_PAGE;
    const pageJobs = jobs.slice(start, start + ROWS_PER_PAGE);

    // Raycast list row: thumb (dark placeholder, no video) · id + platform·model · factor + QC
    list.innerHTML = pageJobs
      .map((j) => {
        const active = j.job_id === selectedJobId;
        const hasMedia = !!localMediaUrl(batch.batch_id, j.file_name);
        const thumb = hasMedia
          ? `<div class="job-thumb" role="img" aria-label="${escapeHtml(t("localPreview"))}" title="${escapeHtml(t("localPreview"))}">${ICON_PLAY}</div>`
          : `<div class="job-thumb empty" title="${escapeHtml(t("noPreview"))}"></div>`;
        const note = (j.qc_notes || "").trim();
        const titled = rowLabel(j) !== j.job_id; // label already names the condition
        return `
          <div class="job-item${active ? " active" : ""}${titled ? " job-item--titled" : ""}" data-job-id="${escapeHtml(j.job_id)}" role="button" tabindex="0" aria-pressed="${active ? "true" : "false"}"${note ? ` title="${escapeHtml(note)}"` : ""}>
            ${thumb}
            <div class="job-main">
              <span class="job-id" title="${escapeHtml(rowLabelTip(j))}">${escapeHtml(rowLabel(j))}</span>
              <span class="job-sub" title="${escapeHtml(platformModelLabel(j))}">${modelSubHtml(platformModelLabel(j))}</span>
            </div>
            <div class="job-tags">
              ${titled ? "" : `<span class="badge cond" title="${escapeHtml(factorLabel(j))}">${escapeHtml(factorLabel(j))}</span>`}
              <span class="badge qc-weak qc-${escapeHtml(j.qc_status)}" data-card-qc>${escapeHtml(qcLabel(j.qc_status))}</span>
            </div>
          </div>`;
      })
      .join("");
    renderPager(jobs.length, start, start + pageJobs.length);
    const key = `${batch.batch_id}|${currentPage}|${JSON.stringify(getFilters())}`;
    if (key !== listKey) {
      listKey = key;
      list.scrollTop = 0;
    }
  }

  /** Pager: prev · up to 10 page numbers windowed around the current page · next */
  function renderPager(total, from, to) {
    const nav = $("pager");
    if (!nav) return;
    if (!total) {
      nav.innerHTML = "";
      return;
    }
    const pages = pageCount(total);
    let lo = Math.max(1, currentPage - Math.floor(PAGER_MAX_NUMBERS / 2) + 1);
    let hi = Math.min(pages, lo + PAGER_MAX_NUMBERS - 1);
    lo = Math.max(1, hi - PAGER_MAX_NUMBERS + 1);
    let nums = "";
    for (let n = lo; n <= hi; n++) {
      nums += `<button type="button" class="pager-btn${n === currentPage ? " is-on" : ""}" data-page="${n}"${n === currentPage ? ' aria-current="page"' : ""}>${n}</button>`;
    }
    nav.innerHTML = `
      <span class="pager-count mono">${from + 1}–${to} / ${total}</span>
      <span class="pager-pages">
        <button type="button" class="pager-btn" data-page="${currentPage - 1}" aria-label="${escapeHtml(t("prevPage"))}"${currentPage <= 1 ? " disabled" : ""}>${ICON_PREV}</button>
        ${nums}
        <button type="button" class="pager-btn" data-page="${currentPage + 1}" aria-label="${escapeHtml(t("nextPage"))}"${currentPage >= pages ? " disabled" : ""}>${ICON_NEXT}</button>
      </span>`;
  }

  /** QC card (always visible at the bottom of Detail). */
  function qcMarkup(readOnly) {
    return `
      <div class="annot-block" id="annot-block">
        ${readOnly ? `<p class="annot-readonly-hint">${escapeHtml(t("deprecatedRo"))}</p>` : ""}
        <span class="sr-only" id="annot-status-label">${escapeHtml(t("qc"))}</span>
        <div class="qc-seg-host" id="annot-seg-root" data-readonly="${readOnly ? "1" : "0"}"></div>
      </div>`;
  }

  /** Note editor (lives in the 备注 popover). */
  function noteMarkup(job, readOnly) {
    const notes = job.qc_notes || "";
    const len = notes.length;
    const showCount = len > NOTE_COUNT_AT;
    return `
      <div class="note-row">
        <input
          type="text"
          id="annot-note"
          class="note-input"
          maxlength="${NOTE_MAX}"
          placeholder="${escapeHtml(t("notePh"))}"
          value="${escapeHtml(notes)}"
          ${readOnly ? "disabled" : ""}
          autocomplete="off"
        />
        <div class="note-meta">
          <span>${readOnly ? escapeHtml(t("deprecatedRo")) : ""}</span>
          <span id="annot-count" class="note-count"${showCount ? "" : ' hidden=""'}>${len}/${NOTE_MAX}</span>
        </div>
      </div>`;
  }

  function popoverMarkup(name, title, body) {
    return `
      <div class="popover hidden" id="pop-${name}" role="dialog" aria-label="${escapeHtml(title)}">
        <div class="popover-head"><span>${escapeHtml(title)}</span><kbd>esc</kbd></div>
        <div class="popover-body">${body}</div>
      </div>`;
  }

  function chipMarkup(name, label, hasContent) {
    return `<button type="button" class="detail-chip${hasContent ? " has-content" : ""}" data-pop="${name}" aria-expanded="false" aria-controls="pop-${name}">${escapeHtml(label)}${ICON_CHEVRON}</button>`;
  }

  /** Player frame: 16:9 video sized to the space left; popovers overlay this area. */
  function playerMarkup(batch, job, overlays) {
    const urls = mediaUrls(batch.batch_id, job.file_name);
    const mediaUrl = urls && urls.primary;
    const fallbackAttr = urls && urls.fallback ? ` data-fallback-src="${escapeHtml(urls.fallback)}"` : "";
    const body = mediaUrl
      ? `<video id="detail-video" controls preload="metadata" playsinline src="${escapeHtml(mediaUrl)}"${fallbackAttr} title="${escapeHtml(job.file_name || "")}">
           ${escapeHtml(t("videoNo"))}
         </video>`
      : `<div class="detail-video-empty" aria-label="${escapeHtml(t("noPreview"))}">
           <span>${escapeHtml(t("noPreview"))}</span>
           <span class="muted mono" style="font-size:11px">media/${escapeHtml(batch.batch_id)}/…</span>
         </div>`;
    return `<div class="detail-video" aria-label="${escapeHtml(t("player"))}">${body}${overlays}</div>`;
  }

  function techMarkup(batch, job) {
    const folderLink = batch.drive_folder_id
      ? `<a class="drive-ext" href="${driveFolderUrl(batch.drive_folder_id)}" target="_blank" rel="noopener">${ICON_EXT} ${escapeHtml(t("openFolder"))}</a>`
      : "—";
    const fileLink = job.drive_file_id
      ? `<a class="drive-ext" href="${driveFileUrl(job.drive_file_id)}" target="_blank" rel="noopener">${ICON_EXT} ${escapeHtml(t("openFile"))}</a>`
      : "—";
    const pathText = batch.drive_path
      ? `${batch.drive_path}${job.file_name || ""}`
      : job.file_name || "—";
    const previz = resolvePreviz(job);
    const previzDd = previz
      ? `<span title="${escapeHtml(previz.file || "")}">${escapeHtml(previz.name)}</span>${
          previz.file && previz.file !== previz.name
            ? ` <span class="muted mono tech-secondary">${escapeHtml(previz.file)}</span>`
            : ""
        }`
      : "—";

    const media = localMediaUrl(batch.batch_id, job.file_name);
    return `
        <dl class="tech-dl">
          <dt>${escapeHtml(t("model"))}</dt><dd>${escapeHtml(platformModelLabel(job))}</dd>
          ${uploadIdentityRows(job)}
          <dt>${escapeHtml(t("resolution"))}</dt><dd>${escapeHtml(job.size || "—")}</dd>
          <dt>${escapeHtml(t("fileSize"))}</dt><dd>${escapeHtml(formatBytes(job.bytes))}</dd>
          <dt>${escapeHtml(t("file"))}</dt><dd>${escapeHtml(job.file_name || "—")}</dd>
          <dt>${escapeHtml(t("taskId"))}</dt><dd>${escapeHtml(job.kling_task_id || "—")}</dd>
          <dt>${escapeHtml(t("path"))}</dt><dd>${escapeHtml(pathText)}</dd>
          <dt>${escapeHtml(t("drive"))}</dt><dd>${fileLink} · ${folderLink}</dd>
          <dt>${escapeHtml(t("previz"))}</dt><dd>${previzDd}</dd>
          <dt>${escapeHtml(t("jobStatus"))}</dt><dd>${escapeHtml(job.job_status || "—")}</dd>
          <dt>${escapeHtml(t("mediaSrc"))}</dt><dd>${escapeHtml(media || "—")}</dd>
        </dl>`;
  }

  function applyQcStatusChange(batch, job, next) {
    if (!next || next === job.qc_status) return;
    const result = persistAnnotation(batch.batch_id, job.job_id, { qc_status: next });
    if (!result.ok) {
      showAnnotFeedback(false, t("saveFailed"));
      syncAnnotationChrome(job);
      return;
    }
    syncAnnotationChrome(job);
    flashSaved();
    patchCardChrome(job);
    refreshCardListQuiet(batch);
  }

  function mountR13Segment(batch, job) {
    const host = $("annot-seg-root");
    const api = window.__aigcQc;
    if (!host || !api || typeof api.mount !== "function") return;
    const readOnly = batch.batch_status === "deprecated";
    api.mount(host, {
      value: job.qc_status, // "pending" (or missing) = nothing selected, no thumb
      disabled: readOnly,
      labelledBy: "annot-status-label",
      labels: { pass: t("pass"), partial: t("partial"), fail: t("fail") },
      onChange: (next) => applyQcStatusChange(batch, job, next),
    });
  }

  function bindAnnotationHandlers(batch, job) {
    const readOnly = batch.batch_status === "deprecated";
    mountR13Segment(batch, job);
    if (readOnly) return;

    const input = $("annot-note");
    if (!input) return;

    const saveNotes = () => {
      const val = input.value.slice(0, NOTE_MAX);
      if (val === (job.qc_notes || "")) {
        updateNoteCount(val.length);
        return;
      }
      const result = persistAnnotation(batch.batch_id, job.job_id, { qc_notes: val });
      if (!result.ok) {
        showAnnotFeedback(false, t("saveFailed"));
        return;
      }
      flashSaved();
      patchCardChrome(job);
      refreshCardListQuiet(batch);
      const chip = document.querySelector('.detail-chip[data-pop="note"]');
      if (chip) chip.classList.toggle("has-content", !!val.trim());
      const row = document.querySelector(`.job-item[data-job-id="${CSS.escape(job.job_id)}"]`);
      if (row) row.title = val.trim();
    };

    input.addEventListener("input", () => {
      const len = input.value.length;
      updateNoteCount(len);
      showAnnotFeedback(true, "");
      if (noteDebounceTimer) clearTimeout(noteDebounceTimer);
      noteDebounceTimer = setTimeout(saveNotes, DEBOUNCE_MS);
    });

    input.addEventListener("blur", () => {
      if (noteDebounceTimer) {
        clearTimeout(noteDebounceTimer);
        noteDebounceTimer = null;
      }
      saveNotes();
    });

    input.addEventListener("keydown", (e) => {
      if (e.key === "Enter") {
        e.preventDefault();
        if (noteDebounceTimer) {
          clearTimeout(noteDebounceTimer);
          noteDebounceTimer = null;
        }
        saveNotes();
        input.blur();
      }
    });
  }

  function updateNoteCount(len) {
    const el = $("annot-count");
    if (!el) return;
    el.textContent = `${len}/${NOTE_MAX}`;
    if (len > NOTE_COUNT_AT) el.removeAttribute("hidden");
    else el.setAttribute("hidden", "");
  }

  function showAnnotFeedback(ok, msg) {
    const el = $("annot-feedback");
    if (!el) return;
    if (savedFlashTimer) {
      clearTimeout(savedFlashTimer);
      savedFlashTimer = null;
    }
    el.textContent = msg || "";
    el.classList.toggle("note-saved", ok);
    el.classList.toggle("note-error", !ok);
    el.classList.toggle("show", !!msg);
  }

  function flashSaved() {
    const token = t("saved");
    showAnnotFeedback(true, token);
    savedFlashTimer = setTimeout(() => {
      const el = $("annot-feedback");
      if (el && el.textContent === token) {
        el.classList.remove("show");
      }
    }, SAVED_FLASH_MS);
  }

  function syncAnnotationChrome(job) {
    const batch = getSelectedBatch();
    if (!batch) return;
    mountR13Segment(batch, job);
  }

  /** Keep the list row's QC label in sync (note lives in Detail now). */
  function patchCardChrome(job) {
    const row = document.querySelector(`.job-item[data-job-id="${CSS.escape(job.job_id)}"]`);
    const badge = row && row.querySelector("[data-card-qc]");
    if (!badge) return;
    badge.className = `badge qc-weak qc-${job.qc_status}`;
    badge.textContent = qcLabel(job.qc_status);
  }

  function refreshCardListQuiet(batch) {
    const f = getFilters();
    if (f.qc && selectedJobId) {
      const job = (batch.jobs || []).find((j) => j.job_id === selectedJobId);
      if (job && job.qc_status !== f.qc) {
        // keep visible until user changes filter
      }
    }
  }

  function renderFindings(batch) {
    const block = $("finding-block");
    const host = $("finding-rows");
    if (!block || !host) return;
    const src = loc(batch, "findings");
    const rows = (Array.isArray(src) ? src : []).filter((row) => {
      if (!row || typeof row !== "object") return false;
      return String(row.label || "").trim() || String(row.text || "").trim();
    });
    const band = block.closest(".batch-band");
    if (!rows.length) {
      block.classList.add("hidden");
      if (band) band.classList.remove("has-findings");
      block.removeAttribute("title");
      host.innerHTML = "";
      return;
    }
    block.classList.remove("hidden");
    if (band) band.classList.add("has-findings");
    host.innerHTML = rows
      .map((row) => {
        const label = String(row.label || "").trim();
        const text = String(row.text || "").trim();
        const sep = label && text ? (lang() === "en" ? ": " : "：") : "";
        // One line per finding; full text in the tooltip if it gets clamped
        return `<p class="finding-row" title="${escapeHtml(label + sep + text)}">${label ? `<strong>${escapeHtml(label)}</strong>` : ""}${sep}${escapeHtml(text)}</p>`;
      })
      .join("");
    // Whole set as tooltip too, in case extra rows are clipped by the band height
    block.title = rows.map((row) => [String(row.label || "").trim(), String(row.text || "").trim()].filter(Boolean).join(lang() === "en" ? ": " : "：")).join("\n");
  }

  function renderDetail() {
    const batch = getSelectedBatch();
    const placeholder = $("detail-placeholder");
    const jobDetail = $("job-detail");
    const jobQc = $("job-qc");
    const clearJob = () => {
      openPop = null;
      jobDetail.classList.add("hidden");
      jobDetail.innerHTML = "";
      jobQc.classList.add("hidden");
      jobQc.innerHTML = "";
    };

    if (!batch) {
      placeholder.classList.remove("hidden");
      placeholder.textContent = t("selectBatchOrJob");
      clearJob();
      if (window.__aigcQc && typeof window.__aigcQc.unmount === "function") {
        window.__aigcQc.unmount();
      }
      renderFindings({ findings: [] });
      detailJobRendered = null;
      return;
    }

    placeholder.classList.add("hidden");
    renderFindings(batch);

    const job = (batch.jobs || []).find((j) => j.job_id === selectedJobId);
    if (!job) {
      clearJob();
      if (window.__aigcQc && typeof window.__aigcQc.unmount === "function") {
        window.__aigcQc.unmount();
      }
      detailJobRendered = null;
      // Always show empty hint when no job is selected (fixes stuck empty Detail)
      placeholder.classList.remove("hidden");
      placeholder.textContent = selectedJobId
        ? `${t("jobMissing")}: ${selectedJobId}`
        : t("selectJob");
      return;
    }

    // Selected job → Detail must hydrate (identity + video + QC)
    placeholder.classList.add("hidden");
    jobDetail.classList.remove("hidden");
    jobQc.classList.remove("hidden");

    const renderKey = `${batch.batch_id}::${job.job_id}`;

    if (detailJobRendered === renderKey && $("annot-block")) {
      syncAnnotationChrome(job);
      const input = $("annot-note");
      if (input && document.activeElement !== input) {
        input.value = job.qc_notes || "";
        updateNoteCount(input.value.length);
      }
      return;
    }

    if (window.__aigcQc && typeof window.__aigcQc.unmount === "function") {
      window.__aigcQc.unmount();
    }
    const readOnly = batch.batch_status === "deprecated";

    // Fixed Detail: head (+ 备注/技术信息 chips) → player (all free height) → QC
    openPop = null;
    const overlays =
      popoverMarkup("note", t("note"), noteMarkup(job, readOnly)) +
      popoverMarkup("tech", t("tech"), techMarkup(batch, job));
    jobDetail.innerHTML = `
      <div class="detail-job-head">
        <strong class="mono" title="${escapeHtml(rowLabelTip(job))}">${escapeHtml(rowLabel(job))}</strong>
        <span class="badge route-${escapeHtml(job.route || "")}" title="${escapeHtml(platformModelLabel(job))}">${modelSubHtml(platformModelLabel(job))}</span>
        ${rowLabel(job) !== job.job_id ? "" : `<span class="badge cond" title="${escapeHtml(factorLabel(job))}">${escapeHtml(factorLabel(job))}</span>`}
        <span class="detail-chips">
          <span id="annot-feedback" class="note-saved" aria-live="polite"></span>
          ${chipMarkup("note", t("note"), !!(job.qc_notes || "").trim())}
          ${chipMarkup("tech", t("tech"), false)}
        </span>
      </div>
      ${promptRow(job)}
      ${playerMarkup(batch, job, overlays)}`;
    jobQc.innerHTML = qcMarkup(readOnly);

    detailJobRendered = renderKey;
    bindAnnotationHandlers(batch, job);
  }

  function setPop(name) {
    const next = name && name !== openPop ? name : null;
    const active = document.activeElement;
    if (active && active.closest && active.closest(".popover") && !next) active.blur(); // saves the note
    openPop = next;
    document.querySelectorAll("#job-detail .popover").forEach((el) => {
      el.classList.toggle("hidden", el.id !== `pop-${openPop}`);
    });
    document.querySelectorAll("#job-detail .detail-chip").forEach((chip) => {
      const on = chip.getAttribute("data-pop") === openPop;
      chip.classList.toggle("is-on", on);
      chip.setAttribute("aria-expanded", on ? "true" : "false");
    });
    if (openPop === "note") {
      const input = $("annot-note");
      if (input && !input.disabled) {
        input.focus();
        input.setSelectionRange(input.value.length, input.value.length);
      }
    }
  }

  /** Chips toggle; click outside or Esc closes. */
  function bindPopovers() {
    $("job-detail").addEventListener("click", (e) => {
      const chip = e.target.closest(".detail-chip");
      if (chip) setPop(chip.getAttribute("data-pop"));
    });
    document.addEventListener("click", (e) => {
      if (!openPop) return;
      if (e.target.closest(".popover") || e.target.closest(".detail-chip")) return;
      setPop(null);
    });
    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape" && openPop) {
        e.preventDefault();
        setPop(null);
      }
    });
  }

  function renderAll() {
    renderTopbar();
    renderModelFilter();
    renderBatchSelector();
    renderJobs();
    renderDetail();
    fitBand();
  }

  function paintBatchTitle(batch) {
    const el = $("jobs-title");
    if (!el) return;
    const text = batch ? batchTitle(batch) : t("jobs");
    el.textContent = text;
    el.title = text;
  }

  /**
   * Title band keeps one fixed height (126px). A hypothesis that needs two lines
   * switches the band to a compact title size so both still fit.
   */
  function fitBand() {
    const band = document.querySelector(".batch-band");
    const hyp = $("batch-hypothesis");
    if (!band || !hyp) return;
    if (document.activeElement === hyp) return;
    const lh = parseFloat(getComputedStyle(hyp).lineHeight) || 20;
    band.classList.toggle("band-2line", !hyp.classList.contains("hidden") && hyp.scrollHeight > lh * 1.5);
  }

  function paintStatus() {
    const el = $("load-status");
    if (!el) return;
    if (loadState === "fail") {
      el.textContent = t("loadFailedStatus");
      return;
    }
    if (loadState === "ok") {
      el.textContent = `${t("ok")} · ${VERSION} · ${new Date().toLocaleTimeString(timeLocale(), { hour12: false })}`;
      return;
    }
    el.textContent = t("loading");
  }

  async function loadData() {
    loadState = "loading";
    paintStatus();
    try {
      const bust = new URL(DATA_URL, location.href);
      bust.searchParams.set("t", String(Date.now()));
      const res = await fetch(bust.toString(), { cache: "no-store" });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      data = await res.json();
      applyLocalOverrides();
      detailJobRendered = null;
      const selected = (data.batches || []).find((b) => b.batch_id === selectedBatchId);
      if (!selected || isHiddenTestBatch(selected)) {
        const prefer =
          visibleBatches().find((b) => b.batch_status !== "deprecated") ||
          visibleBatches()[0];
        selectedBatchId = prefer ? prefer.batch_id : null;
        selectedJobId = null;
      }
      renderAll();
      loadState = "ok";
      paintStatus();
    } catch (err) {
      console.error(err);
      loadState = "fail";
      paintStatus();
      paintBatchTitle(null);
      paintBatchHypothesis(null);
      $("job-list").innerHTML = "";
      renderPager(0);
      $("jobs-empty").classList.remove("hidden");
      $("jobs-empty").textContent = t("loadFailed");
    }
  }

  function isTypingTarget(el) {
    if (!el) return false;
    const tag = el.tagName;
    if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return true;
    if (el.isContentEditable) return true;
    return false;
  }

  /** Up/Down moves the selection (flipping pages); Enter plays the selected video. */
  function moveSelection(delta) {
    const jobs = filteredJobs(getSelectedBatch());
    if (!jobs.length) return;
    const cur = jobs.findIndex((j) => j.job_id === selectedJobId);
    const next = cur < 0 ? (currentPage - 1) * ROWS_PER_PAGE : Math.min(jobs.length - 1, Math.max(0, cur + delta));
    if (next === cur) return;
    currentPage = Math.floor(next / ROWS_PER_PAGE) + 1;
    selectJob(jobs[Math.min(next, jobs.length - 1)].job_id);
    const row = document.querySelector(".job-item.active");
    if (row) row.scrollIntoView({ block: "nearest" });
  }

  function playSelected() {
    const v = $("detail-video");
    if (v) v.play().catch(() => {});
  }

  function bindKeyboard() {
    document.addEventListener("keydown", (e) => {
      // "/" and 1/2/3 still work right after clicking a QC segment (focus sits on its radio input)
      const onRadio = e.target && e.target.type === "radio";
      if (isTypingTarget(e.target) && !(onRadio && (e.key === "/" || e.key === "0" || e.key === "1" || e.key === "2" || e.key === "3"))) return;
      if (e.key === "ArrowDown" || e.key === "ArrowUp") {
        if (e.altKey || e.ctrlKey || e.metaKey) return;
        e.preventDefault();
        moveSelection(e.key === "ArrowDown" ? 1 : -1);
        return;
      }
      if (e.key === "Enter") {
        const tag = e.target && e.target.tagName;
        if (tag === "BUTTON" || tag === "A" || tag === "SUMMARY" || tag === "VIDEO") return;
        if (!selectedJobId) return;
        e.preventDefault();
        // A focused row selects itself first (list handler runs before this bubbles up)
        playSelected();
        return;
      }
      if (!selectedJobId) return;
      const batch = getSelectedBatch();
      const job = getSelectedJob();
      if (!batch || !job) return;
      if (batch.batch_status === "deprecated") return;

      if (e.key === "0" || e.key === "1" || e.key === "2" || e.key === "3") {
        e.preventDefault();
        const map = { "0": "pending", "1": "pass", "2": "partial", "3": "fail" };
        // Same key as the current result undoes it (matches clicking the selected button)
        const next = map[e.key] === job.qc_status ? "pending" : map[e.key];
        applyQcStatusChange(batch, job, next);
        return;
      }

      if (e.key === "/") {
        e.preventDefault();
        if (openPop !== "note") setPop("note");
      }
    });
  }

  function selectJob(jobId) {
    if (!jobId) return;
    if (selectedJobId !== jobId) {
      detailJobRendered = null;
    }
    selectedJobId = jobId;
    renderAll();
  }

  /** Event delegation: survives list innerHTML rebuilds; always hydrates Detail. */
  function bindJobListSelection() {
    const list = $("job-list");
    if (!list || list.dataset.selectionBound === "1") return;
    list.dataset.selectionBound = "1";
    list.addEventListener("click", (e) => {
      const row = e.target.closest(".job-item");
      if (!row || !list.contains(row)) return;
      const id = row.getAttribute("data-job-id");
      if (id) selectJob(id);
    });
    list.addEventListener("keydown", (e) => {
      if (e.key !== "Enter" && e.key !== " ") return;
      const row = e.target.closest(".job-item");
      if (!row || !list.contains(row)) return;
      if (e.key === " ") e.preventDefault();
      const id = row.getAttribute("data-job-id");
      if (id && id !== selectedJobId) selectJob(id);
    });
    $("pager").addEventListener("click", (e) => {
      const btn = e.target.closest("button[data-page]");
      if (!btn || btn.disabled) return;
      currentPage = Number(btn.getAttribute("data-page")) || 1;
      renderJobs();
    });
  }

  let hypoCancel = false;

  function paintBatchHypothesis(batch) {
    const el = $("batch-hypothesis");
    if (!el) return;
    if (!batch) {
      if (document.activeElement === el) el.blur();
      el.classList.add("hidden");
      delete el.dataset.batchId;
      el.textContent = "";
      return;
    }
    if (document.activeElement === el && el.dataset.batchId && el.dataset.batchId !== batch.batch_id) {
      el.blur();
    }
    el.classList.remove("hidden");
    el.dataset.batchId = batch.batch_id;
    el.dataset.placeholder = t("hypoPh");
    el.setAttribute("aria-label", t("hypothesis"));
    if (document.activeElement === el) return;
    const text = oneLine(loc(batch, "hypothesis"));
    el.textContent = text;
    el.title = text;
  }

  function saveBatchHypothesis(batchId, text) {
    const clean = oneLine(text);
    const field = lang() === "en" ? "en" : "zh";
    const batch = (data && data.batches || []).find((b) => b.batch_id === batchId);
    if (batch) batch[field === "en" ? "hypothesis_en" : "hypothesis"] = clean;
    const store = loadHypoStore();
    const entry = hypoEntry(store[batchId]);
    entry[field] = clean;
    store[batchId] = entry;
    localStorage.setItem(LS_HYPO, JSON.stringify(store));
    return clean;
  }

  function bindHypothesisEdit() {
    const el = $("batch-hypothesis");
    if (!el || el.dataset.bound === "1") return;
    el.dataset.bound = "1";
    el.addEventListener("keydown", (e) => {
      if (e.key === "Enter") {
        e.preventDefault();
        el.blur();
      } else if (e.key === "Escape") {
        e.preventDefault();
        hypoCancel = true;
        const batch = (data && data.batches || []).find((b) => b.batch_id === el.dataset.batchId);
        el.textContent = batch ? oneLine(loc(batch, "hypothesis")) : "";
        el.blur();
      }
    });
    el.addEventListener("blur", () => {
      const id = el.dataset.batchId;
      if (!id) return;
      if (hypoCancel) {
        hypoCancel = false;
        return;
      }
      const clean = oneLine(el.textContent);
      el.textContent = clean;
      const batch = (data && data.batches || []).find((b) => b.batch_id === id);
      if (batch && oneLine(loc(batch, "hypothesis")) === clean) {
        fitBand();
        return;
      }
      saveBatchHypothesis(id, clean);
      if (batch) paintBatchHypothesis(batch);
      fitBand();
    });
    el.addEventListener("paste", (e) => {
      e.preventDefault();
      const text = oneLine((e.clipboardData || window.clipboardData).getData("text"));
      document.execCommand("insertText", false, text);
    });
  }

  function bindFilters() {
    ["filter-batch-status", "filter-route", "filter-qc", "filter-cond"].forEach((id) => {
      $(id).addEventListener("change", () => {
        selectedJobId = null;
        detailJobRendered = null;
        currentPage = 1;
        renderAll();
      });
    });
    $("btn-reload").addEventListener("click", () => loadData());
  }

  function syncLocaleChrome() {
    const I = window.__AIGC_I18N;
    if (I && typeof I.apply === "function") I.apply(document);
    document.title = t("docTitle");
    paintStatus();
    if (loadState === "fail") {
      paintBatchTitle(null);
      paintBatchHypothesis(null);
      $("jobs-empty").classList.remove("hidden");
      $("jobs-empty").textContent = t("loadFailed");
    }
  }

  document.querySelectorAll(".lang-switch [data-lang]").forEach((btn) => {
    btn.addEventListener("click", () => {
      const I = window.__AIGC_I18N;
      if (!I) return;
      const next = btn.getAttribute("data-lang");
      if (!next || next === I.getLocale()) return;
      I.setLocale(next);
      detailJobRendered = null;
      syncLocaleChrome();
      if (data) renderAll();
    });
  });

  function bindVideoFallback() {
    const panel = $("job-detail");
    if (!panel || panel.dataset.mediaFallback === "1") return;
    panel.dataset.mediaFallback = "1";
    panel.addEventListener(
      "error",
      (e) => {
        const v = e.target;
        if (!v || v.tagName !== "VIDEO") return;
        const alt = v.getAttribute("data-fallback-src");
        if (!alt || v.dataset.fellback === "1") return;
        v.dataset.fellback = "1";
        v.src = alt;
      },
      true
    );
  }

  bindFilters();
  bindHypothesisEdit();
  bindJobListSelection();
  bindPopovers();
  bindVideoFallback();
  bindKeyboard();
  let fitRaf = 0;
  window.addEventListener("resize", () => {
    cancelAnimationFrame(fitRaf);
    fitRaf = requestAnimationFrame(fitBand);
  });
  syncLocaleChrome();
  loadData();
})();
